import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { HomeService } from 'src/app/service/home.service';
import { EventService } from 'src/app/service/event.service';
import { Slot } from 'src/app/model/slot.model';
import { ROUTES } from 'src/app/route/routes';
import { UserService } from 'src/app/service/user.service';
import { SpinnerService } from 'src/app/components/spinner/spinner.service';
import { Subject } from 'rxjs';
import { FluxService } from 'src/app/service/flux-service';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false
})
export class CalendarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isComplete: boolean = false;
  canGoBack: boolean = true;
  isMassive: boolean = false;

  selectedDate: Date | null;
  selectedSlot: Slot;

  orari_mattino: Slot[] = [];
  orari_pomeriggio: Slot[] = [];
  orari_sera: Slot[] = [];

  orari_mattino_disabled: boolean = false;
  orari_pomeriggio_disabled: boolean = false;
  orari_sera_disabled: boolean = false;

  event_name: any;
  private uuid_event: string;
  public old_number_of_users?: number;
  private old_selected_slot_id?: string;
  private uuid_passenger?: string;
  private uuid_employee?: string;
  private is_waiting_list!: boolean;

  minDate: Date;
  maxDate: Date;
  availableDates: Array<Date> = new Array<Date>();

  isMattinoExpanded: boolean = false;
  isPomeriggioExpanded: boolean = false;
  isSeraExpanded: boolean = false;

  private _oldSlot?: Slot;
  get oldMassiveSlot() {
    if (!this.isMassive || this.canGoBack || !this._oldSlot) return;

    return this._oldSlot;
  }

  userLanguage: string;
  convertTime = Slot.convertTime;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private eventService: EventService,
    private homeService: HomeService,
    private userService: UserService,
    private spinner: SpinnerService,
    private fluxService: FluxService
  ) {
    if (this.userService.isEmployeeWithoutPassenger()) {
      this.isMassive = true;
    }
  }

  ngOnInit() {
    this.userLanguage = this.userService.lang;
    this.fluxService.initializeFromRoute(this.route);
    this.checkBookedReservation();
    this.loadAvailableDays();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAvailableDays() {
    let eventObject = this.homeService.getEventObject();
    if (eventObject) {
      this.uuid_event = eventObject.uuid_event;
      this.event_name = eventObject.event_name[this.userLanguage];
      this.is_waiting_list = eventObject.is_waiting_list!;
      eventObject.event_dates.forEach((el) => {
        let splittedDate = el.split('/');
        this.availableDates.push(
          new Date(
            parseInt(splittedDate[2]),
            parseInt(splittedDate[1]) - 1,
            parseInt(splittedDate[0])
          )
        );
      });

      this.minDate = this.availableDates[0];
      this.maxDate = this.availableDates.at(-1)!;

      this.selectedDate = this.minDate;

      this.handleDateSelection();
    } else {
      this.goTo('events');
    }
  }

  private checkBookedReservation() {
    let res = this.homeService.getEditReservationObject();
    if (res) {
      let maybeProfile = this.homeService.getProfileObject();
      if (maybeProfile && maybeProfile.length == 1) {
        this.uuid_passenger = maybeProfile[0].uuid_passenger;
        this.uuid_employee = maybeProfile[0].uuid_employee;
      }

      this.old_number_of_users = res.old_number_of_users;
      this.old_selected_slot_id = res.uuid_old_event_occurrence;
      this.canGoBack = false;
    }
  }

  dateFilter = (d: Date): boolean => {
    let tmp = this.availableDates.find((el) => {
      return (
        d.getDate() == el.getDate() &&
        d.getMonth() == el.getMonth() &&
        d.getFullYear() == el.getFullYear()
      );
    });

    return this.availableDates && tmp != undefined;
  };

  handleTimeSelection(slot: any) {
    this.orari_mattino.forEach((el) => {
      el.selected = false;
    });
    this.orari_pomeriggio.forEach((el) => {
      el.selected = false;
    });
    this.orari_sera.forEach((el) => {
      el.selected = false;
    });

    slot.selected = true;
    this.selectedSlot = slot;
    this.isComplete = true;
  }

  private handleDisabled(
    orari_morning: any,
    orari_afternoon: any,
    orari_night: any
  ) {
    if (orari_morning.length == 0) {
      this.orari_mattino_disabled = true;
      this.isMattinoExpanded = false;
    } else {
      this.orari_mattino_disabled = false;
    }

    if (orari_afternoon.length == 0) {
      this.orari_pomeriggio_disabled = true;
      this.isPomeriggioExpanded = false;
    } else {
      this.orari_pomeriggio_disabled = false;
    }

    if (orari_night.length == 0) {
      this.orari_sera_disabled = true;
      this.isSeraExpanded = false;
    } else {
      this.orari_sera_disabled = false;
    }
  }

  handleDateSelection() {
    if (!this.selectedDate) return;

    this.isMattinoExpanded = true;
    this.isPomeriggioExpanded = true;
    this.isSeraExpanded = true;

    this.orari_mattino_disabled = true;
    this.orari_pomeriggio_disabled = true;
    this.orari_sera_disabled = true;

    this.orari_mattino = [];
    this.orari_pomeriggio = [];
    this.orari_sera = [];

    this.isComplete = false;

    this.selectedDate.setHours(12); // Sennò settava 00:00 e poi con la conversione a ISO portava al giorno precedente
    let tmp = this.selectedDate.toISOString().substring(0, 10);

    let uuid_user = this.uuid_passenger
      ? this.uuid_passenger
      : this.uuid_employee;

    this.spinner.show();
    this.eventService
      .getEventSlotsByDate(this.uuid_event, tmp, uuid_user)
      .pipe(
        finalize(() => {
          this.handleDisabled(
            this.orari_mattino,
            this.orari_pomeriggio,
            this.orari_sera
          );
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (res) => {
          res.forEach((el: any) => {
            // Aggiungo il numero di posti della vecchia prenotazione
            // per non bloccare la modifica in caso di 0 posti rimasti
            if (
              el.uuid_event_occurrence == this.old_selected_slot_id &&
              this.old_number_of_users
            ) {
              if (this.is_waiting_list) {
                el.available_users_wl += this.old_number_of_users;
              } else {
                el.available_users += this.old_number_of_users;
              }
            }

            let newSlot = new Slot(
              el.uuid_event_occurrence,
              el.occurrence_start_time,
              el.occurrence_end_time,
              el.ticket_datetime,
              el.ticket_file_name,
              el.available_users,
              el.max_users,
              el.are_reservations_open,
              el.are_reservations_not_closed,
              el.are_reservations_not_closed_wl,
              el.at_least_one_can_reserve,
              el.available_users_wl,
              el.max_users_wl,
              this.selectedDate!,
              el.detail
            );

            if (
              el.uuid_event_occurrence == this.old_selected_slot_id &&
              this.old_number_of_users
            ) {
              this._oldSlot = newSlot;
            }

            if (newSlot.occurrence_start_time < '12:00') {
              this.orari_mattino.push(newSlot);
            } else if (
              newSlot.occurrence_start_time >= '12:00' &&
              newSlot.occurrence_start_time < '18:00'
            ) {
              this.orari_pomeriggio.push(newSlot);
            } else {
              this.orari_sera.push(newSlot);
            }
          });
          this.spinner.hide();
        },
        error: (err) => {
          console.error(err);
          this.spinner.hide();
        },
      });
  }

  goTo(path: string) {
    const currentFlux = this.fluxService.getCurrentFlux();
    switch (path) {
      case 'events': {
        this.router.navigate([currentFlux + '/' + ROUTES.EVENT_LIST.path]);
        break;
      }

      case 'profiles': {
        this.homeService.saveSlotObject(this.selectedSlot);

        if (this.userService.isEmployeeWithoutPassenger()) {
          this.router.navigate([currentFlux + '/' + ROUTES.EVENT_SUMMARY.path]);
        } else {
          this.router.navigate([
            currentFlux + '/' + ROUTES.EVENT_SELECT_PROFILE.path,
          ]);
        }
        break;
      }

      case 'edit': {
        this.router.navigate([currentFlux + '/' + ROUTES.EVENT_EDIT.path]);
        break;
      }
    }
  }

  isSpaFlux(): boolean {
    return this.fluxService.getCurrentFlux().includes('spa');
  }

  printaaa(any : any) : void {
    console.log(any);
  }
}
